import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useToast } from '@/hooks/use-toast'
import { supabase } from '@/integrations/supabase/client'
import { Eye, EyeOff, KeyRound } from 'lucide-react'
import { validatePassword } from '@/utils/inputValidation'
import AuthLayout from '@/components/auth/AuthLayout'

const ResetPassword = () => {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { toast } = useToast()

  useEffect(() => {
    // CRITICAL: Clear reset guard immediately on this page to allow session establishment
    try {
      localStorage.removeItem('hrz_reset_guard')
      console.log('🔓 Reset guard cleared on /reset-password page')
    } catch (e) {
      console.warn('⚠️ Could not clear reset guard', e)
    }

    // Accept tokens from both query and hash fragment and set session
    (async () => {
      const params = new URLSearchParams(window.location.search)
      const hash = window.location.hash?.replace('#', '') || ''
      const hashParams = new URLSearchParams(hash)

      // Supabase sometimes returns either access/refresh tokens or a one-time code
      const accessToken = params.get('access_token') || hashParams.get('access_token')
      const refreshToken = params.get('refresh_token') || hashParams.get('refresh_token')
      const code = params.get('code') || hashParams.get('code')
      const errorFromHash = hashParams.get('error') || params.get('error')
      const errorDesc = hashParams.get('error_description') || params.get('error_description')

      try {
        if (errorFromHash) {
          throw new Error(errorDesc || 'Ungültiger oder abgelaufener Link')
        }

        if (code) {
          // PKCE/Code flow fallback
          const { error: exchErr } = await supabase.auth.exchangeCodeForSession(code)
          if (exchErr) throw exchErr
          console.log('✅ Session established from code')
        } else if (accessToken && refreshToken) {
          const { error: setErr } = await supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken })
          if (setErr) throw setErr
          console.log('✅ Session established from tokens')
        } else {
          // If no params at all, check if a recovery session was already established by the SDK
          const { data } = await supabase.auth.getSession()
          if (!data.session) {
            throw new Error('Link-Parameter fehlen')
          }
          console.log('✅ Existing session found')
        }
      } catch (e: any) {
        console.error('❌ Session establishment failed:', e)
        toast({
          variant: 'destructive',
          title: 'Ungültiger Link',
          description: e?.message || 'Der Link ist ungültig oder abgelaufen. Fordern Sie einen neuen an.'
        })
      }
    })()
  }, [toast])

  const validateForm = () => {
    const newErrors: Record<string, string> = {}
    
    const passwordValidation = validatePassword(password)
    if (!passwordValidation.isValid) {
      newErrors.password = passwordValidation.errors[0]
    }
    
    if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwörter stimmen nicht überein'
    }
    
    return newErrors
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    const validationErrors = validateForm()
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }
    
    setErrors({})
    setIsLoading(true)
    
    try {
      // Verify we have a valid session before trying to update
      const { data: sessionData } = await supabase.auth.getSession()
      if (!sessionData.session) {
        throw new Error('Keine gültige Session gefunden. Bitte verwenden Sie einen neuen Reset-Link.')
      }

      const { error } = await supabase.auth.updateUser({
        password: password
      })
      
      if (error) {
        console.error('Password update error:', error)
        throw error
      }
      
      toast({
        title: "Passwort aktualisiert",
        description: "Ihr Passwort wurde erfolgreich zurückgesetzt. Sie können sich jetzt anmelden."
      })
      navigate('/anmelden')
    } catch (error: any) {
      console.error('Reset password error:', error)
      
      // Prefer server-provided error details (Supabase/GoTrue often uses error_description)
      const raw =
        error?.message ||
        error?.error_description ||
        error?.error?.message ||
        error?.data?.message ||
        error?.msg ||
        ''

      let errorMessage =
        raw ||
        "Passwort konnte nicht zurückgesetzt werden. Versuchen Sie es erneut."

      const lower = String(raw).toLowerCase()

      // Provide more specific, user-friendly messages
      if (lower.includes('session') || lower.includes('recovery')) {
        errorMessage = "Session abgelaufen oder ungültig. Bitte fordern Sie einen neuen Reset‑Link an."
      } else if (
        lower.includes('at least') ||
        lower.includes('zu kurz') ||
        lower.includes('minimum')
      ) {
        errorMessage = "Passwort zu kurz. Bitte wählen Sie ein längeres, stärkeres Passwort."
      } else if (
        lower.includes('different') ||
        lower.includes('gleich wie') ||
        lower.includes('old password')
      ) {
        errorMessage = "Neues Passwort muss sich vom alten unterscheiden."
      } else if (lower.includes('weak') || lower.includes('require')) {
        errorMessage = "Das Passwort erfüllt nicht die Sicherheitsanforderungen (Groß-/Kleinbuchstaben, Zahl, Sonderzeichen)."
      }
      
      toast({
        variant: "destructive",
        title: "Fehler",
        description: errorMessage
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (field: string, value: string) => {
    if (field === 'password') {
      setPassword(value)
    } else {
      setConfirmPassword(value)
    }
    
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  return (
    <AuthLayout
      title="Neues Passwort setzen"
      description="Geben Sie Ihr neues Passwort ein"
    >
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5" />
            Passwort zurücksetzen
          </CardTitle>
          <CardDescription>
            Erstellen Sie ein neues, sicheres Passwort
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Neues Passwort</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Neues sicheres Passwort"
                  value={password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  required
                  aria-invalid={errors.password ? 'true' : 'false'}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              {errors.password && (
                <p className="text-sm text-destructive">{errors.password}</p>
              )}
              <p className="text-xs text-muted-foreground">
                Mindestens 8 Zeichen mit Groß-, Kleinbuchstaben und Zahlen
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Passwort bestätigen</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Passwort wiederholen"
                  value={confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                  required
                  aria-invalid={errors.confirmPassword ? 'true' : 'false'}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              {errors.confirmPassword && (
                <p className="text-sm text-destructive">{errors.confirmPassword}</p>
              )}
            </div>

            <Button 
              type="submit" 
              className="w-full bg-gradient-to-r from-primary to-primary-glow hover:shadow-glow transition-all duration-300"
              disabled={isLoading}
            >
              {isLoading ? 'Passwort wird aktualisiert...' : 'Passwort zurücksetzen'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </AuthLayout>
  )
}

export default ResetPassword